//dependencies
import { format, isValid, parseISO } from "date-fns";
import React, { ReactNode } from "react";

// components
import Link from "../../../../../components/Link";
import Typography from "../../../../../components/Text";

// services
import { Goal, GoalHistory } from "../../../../../api/services";

// utils
import { employeeUrlWithFrom } from "../../../../../utils/employeeDetailsBreadcrumbs";
import { getProgressLabels } from "../SingleGoalInfo/utils";

// styles
import css from "./ActivityStreamPanel.module.scss";

// formats the snake case object keys to human readable, or converts the key to the proper string
export const formatSnakeCaseKeyOrChangeKeyToCorrectReadableString = (key: string): string => {
	if (key === "color") {
		return "outlook";
	} else if (key === "value") {
		return "goal's progress value";
	}
	const words = key.split("_");
	return words.join(" ");
};

// format's change value to readable string ready for the sentence
export const formatValue = (value: any, key: string, units: string = ""): string => {
	let formattedValue = "";

	if (
		value === null ||
		value === undefined ||
		value === "" ||
		(Array.isArray(value) && value?.length === 0) ||
		key === "sharers"
	) {
		if (key === "alignment") {
			return "to be removed";
		}
		return "";
	} else if (isValid(parseISO(value)) && (key === "end_date" || key === "start_date")) {
		formattedValue = format(parseISO(value), "MMM d yyyy");
	} else if (key === "color") {
		formattedValue = `be ${value.name}`;
	} else if (key === "categories") {
		const mappedCategories = value.map((category: any) => category.name);
		const isMultipleCategories = mappedCategories.length > 1;
		if (isMultipleCategories) {
			const lastCategory = mappedCategories.pop();
			formattedValue = `be ${mappedCategories.join(", ")} and ${lastCategory}`;
		} else {
			formattedValue = `be ${mappedCategories[0]}`;
		}
	} else if (key === "alignment") {
		formattedValue = `"${value.name}"`;
	} else if (key === "assignee") {
		formattedValue = `${value.name}`;
	} else if (key === "status") {
		if (value === "done") {
			value = "completed";
		}

		formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
	} else if (key === "value" || key === "target") {
		const valueLabels = getProgressLabels({ value, units, decimalPlacesOverride: 2 });
		formattedValue = `${valueLabels?.mainText || ""}`;
	} else if (typeof value === "string") {
		formattedValue = `"${value}"`;
	} else if (typeof value === "number") {
		formattedValue = value.toFixed(2);
	}

	return `to ${formattedValue}`;
};

// formats the changes object in a Goal History Entry to a readable string, handles if changes object has a list of changes
export const formatUpdateChanges = (changes: { key: string; value: any }[], goalUnits: string = "") => {
	let formattedUpdates = "";
	changes.forEach((change, index) => {
		if (change.key === "is_top") {
			formattedUpdates += `the goal to ${change.value ? "be" : "not be"} a top goal`;
		} else {
			formattedUpdates += `the ${formatSnakeCaseKeyOrChangeKeyToCorrectReadableString(change.key)} ${formatValue(
				change.value,
				change.key,
				goalUnits
			)}`;
		}

		// Here we handle if there is more than 1 change, and formatting how that list will appear in the sentence
		if (index === changes.length - 1) {
			formattedUpdates += "";
		} else if (index === changes.length - 2) {
			formattedUpdates += ` and${changes.length > 2 ? "," : ""} `;
		} else {
			formattedUpdates += ", ";
		}
	});

	if (changes.length < 1) {
		formattedUpdates += "the goal";
	}

	return formattedUpdates;
};

// formats the create goal message
const getCreateGoalFormattedUpdate = (entry: GoalHistory) => {
	const formattedCreate = `created the goal "${entry?.original_strategy?.name}" with `;
	const createMessages: string[] = [];
	const { original_strategy: originalGoal } = entry;

	if (originalGoal?.description) {
		createMessages.push(`a description of "${originalGoal?.description}"`);
	}

	if (originalGoal?.target) {
		const valueLabels = getProgressLabels({
			value: originalGoal?.target,
			units: originalGoal?.units,
			decimalPlacesOverride: 2,
		});
		createMessages.push(`target of ${valueLabels?.mainText || ""} ${valueLabels?.subText || ""}`);
	}

	if (originalGoal?.categories && originalGoal?.categories.length > 0) {
		const categoryNames = originalGoal?.categories.map((category, index) =>
			index !== 0 && originalGoal?.categories?.length && index === originalGoal?.categories?.length - 1
				? `and ${category.name}`
				: category.name
		);
		createMessages.push(`categories: ${categoryNames.join(", ")}`);
	}

	if (originalGoal?.goal_weight) {
		createMessages.push(`goal weight ${originalGoal?.goal_weight}`);
	}

	return (
		formattedCreate +
		createMessages
			.map((value, index) => (index !== 0 && index === createMessages.length - 1 ? `and ${value}` : value))
			.join(", ")
	);
};

const UPDATED_KEYS = ["value", "status"];

// formats the goal history update message for the activity stream panel
// it takes a goal entry and builds out a message based on the action and change
export const getGoalHistoryFormattedUpdate = (entry: GoalHistory, goal: Goal) => {
	let formattedUpdate: string | ReactNode = "";
	const { change = {} } = entry;
	// if the change object has an ancestors key, but no alignment key, we translate the most
	// recent as an alignment change and filter out the ancestors key
	if (Object.keys(change).includes("ancestors") && !Object.keys(change).includes("alignment")) {
		change.alignment = change.ancestors.length ? change.ancestors[0] : null;
	}
	const changes = Object.keys(change)
		.map((key) => ({ key, value: change[key] }))
		// ancestors are only used to display alignment
		.filter((change) => change.key !== "ancestors");

	switch (entry?.action) {
		case "update":
			formattedUpdate += Object.keys(change).some((value) => UPDATED_KEYS.includes(value))
				? "updated "
				: "edited ";
			formattedUpdate += formatUpdateChanges(changes, goal?.units);
			break;

		case "create":
			formattedUpdate += getCreateGoalFormattedUpdate(entry);
			break;

		case "remove-sharer":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					removed
					<Link
						href={employeeUrlWithFrom(entry?.sharer?._id)}
						className={css.employeeLink}
						forceHardReload={true}
					>
						<Typography element="span" weight="semi-bold" size="paragraph-3" color="neutral-9">
							{" "}
							{entry?.sharer?.name}{" "}
						</Typography>
					</Link>
					from the goal
				</Typography>
			);
			break;

		case "add-sharer":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					shared the goal with
					<Link
						href={employeeUrlWithFrom(entry?.sharer?._id)}
						className={css.employeeLink}
						forceHardReload={true}
					>
						<Typography element="span" weight="semi-bold" size="paragraph-3" color="neutral-9">
							{" "}
							{entry?.sharer?.name}
						</Typography>
					</Link>
				</Typography>
			);
			break;

		case "add-checklist":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					added {`"${entry?.original_checklist_item?.text}"`} checklist item to the checklist
				</Typography>
			);
			break;

		case "delete-checklist":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					removed {`"${entry?.checklist_item?.text}"`} checklist item from the checklist
				</Typography>
			);
			break;

		case "check-checklist":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					checked {`"${entry?.original_checklist_item?.text}"`} checklist item from the checklist
				</Typography>
			);
			break;

		case "uncheck-checklist":
			formattedUpdate = (
				<Typography element="span" weight="regular" size="paragraph-3">
					unchecked {`"${entry?.original_checklist_item?.text}"`} checklist item from the checklist
				</Typography>
			);
			break;
	}

	return (
		<Typography element="span" weight="regular" size="paragraph-3">
			{formattedUpdate}
		</Typography>
	);
};

// message history entries have no change, but are listed as an "update" with a message
export const isEntryMessageOnly = (entry: GoalHistory) => {
	return entry?.action === "update" && (!entry?.change || Object.keys(entry?.change).length < 1) && !!entry?.message;
};
